<template>
    <page
        id="reports-page"
        title="Reports"
        :status="page_status"
        :waiting="waiting"
        @statused="statused"
    >
        <template v-slot:title><span></span></template>

        <div v-if="report_sets.length > 1" class="uk-margin-top uk-text-small report-sets">
            <ul class="uk-subnav uk-subnav-line">
                <li
                    v-for="report_set in report_sets"
                    :key="report_set.id"
                    :class="{ 'uk-active': (state.current_report_set === report_set.id) }"
                >
                    <a href @click.prevent="set_state_value('current_report_set', report_set.id)">{{ report_set.name }}</a>
                </li>
            </ul>
        </div>
        <div class="uk-tab-scrollable-container- uk-margin-top">
            <ul class="uk-tab uk-text-large">
                <li
                    v-for="report in reports"
                    :key="report.id"
                    :class="{'uk-active': (state.current_report_id === report.id)}"
                >
                    <a href @click.prevent="maybe_change_current_report(report.id)">
                        {{ report.name }}
                    </a>
                </li>
            </ul>
        </div>
        <ul id="report-panels" class="uk-switcher uk-margin">
            <keep-alive>
                <calendar-workload
                    v-if="!state.current_report_id || (state.current_report_id === 'calendar_workload')"
                    class="uk-active"
                />
                <project-workload
                    v-if="(state.current_report_id === 'project_workload')"
                    class="uk-active"
                />
                <budgets
                    v-else-if="(state.current_report_id === 'budgets')"
                    class="uk-active"
                />
                <weekly-workload
                    v-else-if="(state.current_report_id === 'weekly_workload')"
                    class="uk-active"
                />
                <milestones
                    v-else-if="(state.current_report_id === 'milestones')"
                    class="uk-active"
                />
                <office-workload
                    v-else-if="(state.current_report_id === 'office_workload')"
                    class="uk-active"
                />
                <gas-gauge
                    v-else-if="(state.current_report_id === 'gas_gauge')"
                    class="uk-active"
                />
                <executive-dashboard
                    v-else-if="(state.current_report_id === 'executive_dashboard')"
                    class="uk-active"
                />
                <billings
                    v-else-if="(state.current_report_id === 'billings')"
                    class="uk-active"
                />
                <billing-schedule
                    v-else-if="(state.current_report_id === 'billing-schedule')"
                    class="uk-active"
                />
                <prospectives-dashboard
                    v-else-if="(state.current_report_id === 'prospectives_dashboard')"
                    class="uk-active"
                />
                <prospective-projections
                    v-else-if="(state.current_report_id === 'prospective_projections')"
                    class="uk-active"
                />
                <projections
                    v-else-if="(state.current_report_id === 'projections')"
                    class="uk-active"
                />
                <projects-by-types
                    v-else-if="(state.current_report_id === 'projects-by-types')"
                    class="uk-active"
                />
            </keep-alive>
        </ul>
    </page>
</template>

<script type="text/javascript">
import is_page from "../../mixins/IsPage"
import profile_utilities from "../../mixins/ProfileUtilities"
import handles_stored_state from "../../mixins/HandlesStoredState"

import CalendarWorkload from "./CalendarWorkload"
import ProjectWorkload from "./ProjectWorkload"
import Budgets from "./Budgets"
import WeeklyWorkload from "./WeeklyWorkload"
import Milestones from "./Milestones"
import OfficeWorkload from "./OfficeWorkload"
import GasGauge from "./GasGauge"
import ExecutiveDashboard from "./ExecutiveDashboard"
import BillingSchedule from "./BillingSchedule"
import Billings from "./Billings"
import ProspectivesDashboard from "./ProspectivesDashboard"
import ProspectiveProjections from "./ProspectiveProjections"
import Projections from "./Projections"
import ProjectsByTypes from "./ProjectsByTypes"

export default {
    mixins: [is_page, profile_utilities, handles_stored_state],
    components: {
        CalendarWorkload,
        ProjectWorkload,
        Budgets,
        WeeklyWorkload,
        Milestones,
        OfficeWorkload,
        GasGauge,
        BillingSchedule,
        Billings,
        ExecutiveDashboard,
        ProspectivesDashboard,
        ProspectiveProjections,
        Projections,
        ProjectsByTypes
    },
    watch: {
        $route: "reroot",
        "state.current_report_set": "report_set_changed"
    },
    mounted () {
        this.reroot()
    },
    methods: {
        reroot () {
            if(this.$route.query.report_id) {
                let current_report_set = "general"
                let current_report_id = "calendar_workload"
                this.report_sets.forEach(report_set => {
                    const report = report_set.reports.find(report => report.id === this.$route.query.report_id)
                    if(report) {
                        current_report_set = report_set.id
                        current_report_id = report.id
                    }
                })
                this.set_state_value("current_report_set", current_report_set)
                this.maybe_change_current_report(current_report_id)
            }
        },
        report_set_changed () {
            if(this.state.current_report_set) this.maybe_change_current_report(this.reports[0].id)
        },
        change_current_report (report_id) {
            this.set_state_value("current_report_id", report_id)
        },
        maybe_change_current_report (report_id) {
            if(this.state.current_report_id !== report_id) this.change_current_report(report_id)
        }
    },
    computed: {
        state_identifier () {
            return "reports-container"
        },
        report_sets () {
            const report_sets = [
                {
                    id: "individual-workload",
                    name: "Individual Workload",
                    reports: [
                        { id: "project_workload", name: "Personal Workload" },
                        { id: "budgets", name: "Project Budgets" },
                        { id: "calendar_workload", name: "Project Calendar" }
                    ]
                },

                {
                    id: "team-workload",
                    name: "Team Workload",
                    reports: [
                        { id: "office_workload", name: "Team Workload" },
                        { id: "weekly_workload", name: "Weekly Workload" },
                        { id: "milestones", name: "Deliverables" },
                        { id: "projects-by-types", name: "Project Types" },
                        { id: "gas_gauge", name: "Gas Gauge" }
                        // ****** Time off from dashboard
                    ]
                },

                {
                    id: "prospective",
                    name: "Prospective",
                    reports: [
                        { id: "prospectives_dashboard", name: "Prospectives Dashboard" },
                        { id: "prospective_projections", name: "Prospective Projections" }
                    ]
                },

                {
                    id: "management",
                    name: "Management",
                    reports: [
                        { id: "executive_dashboard", name: "Exec. Dashboard" },
                        { id: "billing-schedule", name: "Billing Schedule" },
                        { id: "billings", name: "Billings" },
                        { id: "projections", name: "Projections" }
                    ]
                }
            ]
            return report_sets
        },
        reports () {
            const report_set = this.report_sets.find(report_set => report_set.id === this.state.current_report_set)
            return report_set ? report_set.reports : []
        },
        can_view_admin_reports () {
            return this.is_associate_principal || this.is_principal || this.is_db_admin_active || this.is_developer
        }
    },
    data () {
        return {
            default_state: {
                current_report_set: "individual-workload",
                current_report_id: "project_workload"
            }
        }
    }
}
</script>

<style lang="less">
#reports-page {
    & > .report-sets {
        text-transform: uppercase;

        li {
            &.uk-active {
                font-weight: bold;
            }
            &:nth-child(2) {
                &:before { display: none; }
            }
        }
    }
}
</style>
